import classnames from "classnames";
import React, { SyntheticEvent, useMemo, useState } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { DeliveryTypes } from "@utils/enums";

import { AlgoliaTrackingService } from "@services/algolia/tracking/algoliaTracking.service";

import { useThunkDispatch } from "@redux/dispatcher";
import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { addToCart } from "@redux/modules/api/data/cart/cart.module";
import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { HuboModal } from "@components/General/HuboModal/huboModal.component";
import AddToCartModalComponent from "@components/Modals/AddToCartModal/addToCartModal.component";
import { useAccessories } from "@components/Product/Hooks/accessories.hook";
import ProductTileButtonComponent from "@components/Product/ProductTile/components/button/productTileButton.component";
import { useSearchHit } from "@components/Search/SearchResults/StateComponents/ResultList/searchHit.provider";

type InstantAddToCartProductTileButtonProps = {
    product: ProductTileModel;
    extraOnClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
    cart?: CartModel | null;
    className?: string;
    defaultDeliveryMethod: DeliveryTypes | null;
    defaultQuantity: number | null;
    preferredStore?: string;
};

const InstantAddToCartProductTileButton = ({
    product,
    cart,
    extraOnClick,
    className,
    defaultDeliveryMethod,
    defaultQuantity,
    preferredStore,
}: InstantAddToCartProductTileButtonProps) => {
    const [addToCartLoading, setAddToCartLoading] = useState(false);
    const productInCart = useMemo(() => {
        return cart?.items?.find(item => item.sku === product.sku);
    }, [cart, product]);
    const [showAddToCartModal, setShowAddToCartModal] = useState(false);
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const { accessories, loadAccessories } = useAccessories();

    const { position, queryID } = useSearchHit();

    const handleOnClick = (e: SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (defaultDeliveryMethod && defaultQuantity && !productInCart) {
            setAddToCartLoading(true);
            if (queryID) {
                AlgoliaTrackingService.trackQuery(
                    queryID,
                    product.sku,
                    position,
                );
            }

            loadAccessories(product.sku);

            return dispatch(
                addToCart(
                    {
                        deliveryType: defaultDeliveryMethod,
                        quantity: defaultQuantity,
                        shopId: preferredStore,
                        sku: product.sku,
                    },
                    product,
                ),
            ).then(() => {
                setAddToCartLoading(false);
                if (!isPageTemplate(PageTemplates.SHOPPING_CART)) {
                    setShowAddToCartModal(true);
                }
            });
        }

        return extraOnClick(e);
    };

    const onAddAccessory = (sku: string, product: ProductTileModel) => {
        if (defaultDeliveryMethod) {
            return dispatch(
                addToCart(
                    {
                        deliveryType: defaultDeliveryMethod,
                        quantity: 1,
                        shopId: preferredStore,
                        sku: sku,
                    },
                    product,
                ),
            )
                .then(() => {
                    broadcastMessage({
                        id: "add-to-cart-modal-messages",
                        text: t("product.addtocart.modal.success.addAccessory"),
                        status: "success",
                        dismissible: true,
                    });
                })
                .catch(() => {
                    broadcastMessage({
                        id: "add-to-cart-modal-messages",
                        text: t("product.addtocart.modal.failure.addAccessory"),
                        status: "danger",
                        dismissible: true,
                    });
                });
        }

        return Promise.resolve();
    };

    return (
        <>
            <ProductTileButtonComponent
                onClick={handleOnClick}
                showOutline={!!productInCart}
                className={classnames(
                    "instantAddToCardProductTileButton",
                    className,
                )}
            >
                <i
                    className={classnames("las la-lg", {
                        "la-plus": !productInCart && !addToCartLoading,
                        "la-check": productInCart && !addToCartLoading,
                        "la-spinner la-pulse": addToCartLoading,
                    })}
                ></i>
                <i className="las la-lg la-shopping-cart"></i>
            </ProductTileButtonComponent>
            {showAddToCartModal && (
                <HuboModal
                    title={t("product.addtocart.modal.title")}
                    customTitle={title => {
                        return (
                            <h2
                                className="text-secondary"
                                style={{ wordBreak: "break-word" }}
                            >
                                <i
                                    className="las la-cart-arrow-down"
                                    aria-hidden="true"
                                ></i>
                                {title}
                            </h2>
                        );
                    }}
                    modalState={showAddToCartModal}
                    toggleHandler={() => setShowAddToCartModal(false)}
                    withFooter={false}
                >
                    {product.salesPrice && (
                        <AddToCartModalComponent
                            medias={product.medias}
                            title={product.title}
                            showHR={true}
                            currentPrice={product.salesPrice}
                            strikethroughPrice={product.strikethroughPrice}
                            promotions={product.promotions}
                            salesIncentives={product.salesIncentives}
                            isBoxArticle={product.boxArticle}
                            accessories={accessories}
                            onAddAccessory={onAddAccessory}
                            onClose={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowAddToCartModal(false);
                            }}
                        />
                    )}
                </HuboModal>
            )}
        </>
    );
};
export default InstantAddToCartProductTileButton;
